/**
 * The one seam between the game loop and the Umami beacon (GRA-100). Every
 * call goes through here so the beacon stays optional at every layer: no-op
 * when `window.umami` is absent (no tag injected, an ad blocker, a test under
 * jsdom), and a throw from `umami.track` itself is swallowed rather than
 * reaching the caller. The game loop must never depend on this module
 * succeeding, or even existing.
 *
 * The full event list (GRA-100, GRA-149), deliberately behavioural rather
 * than vanity metrics. None of the properties below ever carry a round id, an
 * instrument, a date, or free text: only the shape of a round, a run, or an
 * outcome.
 *
 * - `round_completed` `{ mode, timeframe, horizon, preset, correct }`: the
 *   core engagement metric, one per commit.
 * - `session_milestone` `{ rounds }`: depth within one browser session (1,
 *   5, 10, 25 rounds), fired once per threshold. The `1` rung is the
 *   first-round completion.
 * - `standing_shown` `{ players }`: whether the Daily challenge's Standing
 *   (GRA-142) had an audience, once per day.
 * - `mode_switched` `{ to }`: which of Predict, Trade or Challenge a player
 *   switched into.
 * - `challenge_started` `{ recorded }`: a Daily challenge run begins, either
 *   the day's one recorded attempt or a practice replay.
 * - `challenge_completed` `{ score, recorded }`: a Daily challenge run ends,
 *   with `score` bucketed (never the raw correct/total) and `recorded`
 *   distinguishing the scored attempt from a practice replay.
 * - `share_result` `{ method, outcome }`: a share of a challenge result,
 *   which path it took (`native`, `clipboard`, `download`) and how it went
 *   (`success`, `cancelled`, `failed`).
 * - `replay_used` `{ control }`: the post-reveal replay was stepped through,
 *   and by which control (`step`, `jump`, `scrub`).
 * - `visit` `{ returning, daysSince }`: one per page load, saying only
 *   whether this browser has been here before and how long ago in a coarse
 *   bucket (`1`, `2-7`, `8-30`, `30+`, or `none` for a first visit). The
 *   timestamp it is derived from never leaves the device.
 * - `book_recommendation_shown` `{ placement }`: the book recommendation was
 *   rendered, at `milestone`, `challenge_result`, or `learn_page`. Once per
 *   placement per page load.
 * - `book_link_clicked` `{ placement }`: an outbound /go/book link was
 *   clicked, from the same three placements. Sent manually, one per click:
 *   the Umami tag carries no automatic outbound-link tracking (its
 *   auto-track only covers pageviews and elements carrying a
 *   `data-umami-event` attribute, which none of these links do), so there is
 *   nothing here to double count.
 * - `pack_viewed` `{ pack }`: a practice pack's page was opened, once per
 *   mount.
 * - `pack_exercise_completed` `{ pack, sample, correct }`: one pack exercise
 *   was called, whether it was one of the free samples, and whether the call
 *   was right. Deliberately not `round_completed`: a pack exercise is not free
 *   play and must not move the engagement metric.
 * - `pack_checkout_started` `{ pack }`: the buy control was pressed and a
 *   Checkout session was asked for. Never an amount, and never a currency.
 * - `pack_purchase_completed` `{ pack }`: a return from Checkout claimed an
 *   access token successfully.
 * - `pack_access_restored` `{ pack }`: the restore-access form recovered a
 *   token for an existing purchase. Never the email it was given.
 */

import { storage as appStorage, type TypedStorage } from "./storage";
import type { Preset } from "../types";

type UmamiTrack = (eventName: string, eventData?: Record<string, unknown>) => void;

declare global {
  interface Window {
    umami?: { track: UmamiTrack };
  }
}

export type AnalyticsMode = "predict" | "trade";
export type AnalyticsTimeframe = "daily" | "weekly" | "monthly";
export type AnalyticsHorizon = "trend" | "nextCandle";

export type RoundCompletedProps = {
  mode: AnalyticsMode;
  timeframe: AnalyticsTimeframe;
  horizon: AnalyticsHorizon;
  preset: Preset;
  correct: boolean;
};

/** Every destination `mode_switched` can report, Challenge included. */
export type AnalyticsDestination = AnalyticsMode | "challenge";

export type ModeSwitchedProps = {
  to: AnalyticsDestination;
};

/**
 * The thresholds a session's round count can cross, each reported once. `1`
 * is the first-round completion: the question "did anyone who arrived
 * actually finish a round" is the same depth question the rest of the ladder
 * asks, so it is a rung on it rather than an event of its own.
 */
const MILESTONES = [1, 5, 10, 25] as const;
export type SessionMilestone = (typeof MILESTONES)[number];

/** In-memory only, by design (spec): a reload starts a fresh session. */
let sessionRoundCount = 0;
let firedMilestones = new Set<SessionMilestone>();

/** Send one event, swallowing any throw so a beacon failure never propagates. */
function send(eventName: string, eventData?: Record<string, unknown>): void {
  try {
    window.umami?.track(eventName, eventData);
  } catch {
    // The beacon is best-effort; the game loop must never see this.
  }
}

/**
 * Record one finished Round: sends `round_completed`, then advances the
 * in-session round count and fires `session_milestone` the first time it
 * crosses 1, 5, 10, or 25. Call exactly once per commit (the round-lifecycle
 * seam in roundLoop.ts already guards against a double `submit`), so every
 * mode, and the daily challenge's fixed run, count without double-counting.
 */
export function trackRoundCompleted(props: RoundCompletedProps): void {
  send("round_completed", props);

  sessionRoundCount += 1;
  for (const threshold of MILESTONES) {
    if (sessionRoundCount >= threshold && !firedMilestones.has(threshold)) {
      firedMilestones.add(threshold);
      send("session_milestone", { rounds: threshold });
    }
  }
}

/** How big the day's crowd was, in three buckets. Never the exact count. */
export type PlayersBucket = "<10" | "10-99" | "100+";

export type StandingShownProps = {
  players: PlayersBucket;
};

/**
 * Record that a Daily challenge result screen showed a Standing (GRA-142),
 * once per day. Only the crowd's size bucket travels: no percentile, no score,
 * no date, nothing that could place one player inside the crowd. It answers
 * one question, whether the feature had an audience at all.
 */
export function trackStandingShown(props: StandingShownProps): void {
  send("standing_shown", props);
}

/** Record a Predict/Trade/Challenge mode switch (not per-timeframe). */
export function trackModeSwitched(props: ModeSwitchedProps): void {
  send("mode_switched", props);
}

export type ChallengeStartedProps = {
  /** True for the day's one scored attempt, false for a practice replay. */
  recorded: boolean;
};

/**
 * Record that a Daily challenge run began (GRA-149): the recorded attempt for
 * a day not yet opened, or a practice replay. Never fires twice for the same
 * recorded attempt (re-entering an already-open day is a no-op at the call
 * site), and fires once per replay.
 */
export function trackChallengeStarted(props: ChallengeStartedProps): void {
  send("challenge_started", props);
}

/** A challenge run's score, bucketed rather than sent as a raw fraction. */
export type ScoreBucket = "low" | "mid" | "high";

/**
 * Bucket a finished run's hit rate into three bands. `total` of 0 buckets as
 * `low` rather than dividing by zero; a real run is never that short.
 */
export function challengeScoreBucket(correct: number, total: number): ScoreBucket {
  if (total <= 0) return "low";
  const ratio = correct / total;
  if (ratio < 0.4) return "low";
  if (ratio < 0.7) return "mid";
  return "high";
}

export type ChallengeCompletedProps = {
  score: ScoreBucket;
  recorded: boolean;
};

/** Record that a Daily challenge run ended (GRA-149), scored or practice. */
export function trackChallengeCompleted(props: ChallengeCompletedProps): void {
  send("challenge_completed", props);
}

export type ShareMethod = "native" | "clipboard" | "download";
export type ShareOutcome = "success" | "cancelled" | "failed";

export type ShareResultProps = {
  method: ShareMethod;
  outcome: ShareOutcome;
};

/** Record how a challenge result share went (GRA-149): which path, how it landed. */
export function trackShareResult(props: ShareResultProps): void {
  send("share_result", props);
}

export type ReplayControl = "step" | "jump" | "scrub";

export type ReplayUsedProps = {
  control: ReplayControl;
};

/** Record one use of the post-reveal replay (GRA-136/GRA-147), by control. */
export function trackReplayUsed(props: ReplayUsedProps): void {
  send("replay_used", props);
}

/** Where the book recommendation can appear. One name per placement, and
 * the same vocabulary the /go/book redirect's `src` allowlist accepts
 * (functions/lib/affiliate.ts) so a link and its event can never disagree. */
export type BookPlacement = "milestone" | "challenge_result" | "learn_page";

export type BookPlacementProps = {
  placement: BookPlacement;
};

/** Placements already reported this page load, so a re-render sends nothing. */
let shownPlacements = new Set<BookPlacement>();

/**
 * Record that the book recommendation was rendered at `placement`, at most
 * once per placement per page load. React re-renders, a panel reopening, or
 * a result screen remounting must not each count as a fresh impression.
 */
export function trackBookRecommendationShown(props: BookPlacementProps): void {
  if (shownPlacements.has(props.placement)) return;
  shownPlacements.add(props.placement);
  send("book_recommendation_shown", { placement: props.placement });
}

/**
 * Record one click on an outbound /go/book link. Fired from the click
 * handler rather than left to the tag: Umami's auto-track only watches
 * elements carrying a `data-umami-event` attribute, so a manual send is the
 * only send and cannot be doubled.
 */
export function trackBookLinkClicked(props: BookPlacementProps): void {
  send("book_link_clicked", { placement: props.placement });
}

/** How long since the last visit, in buckets. `none` is a first visit. */
export type DaysSinceBucket = "1" | "2-7" | "8-30" | "30+" | "none";

export type VisitProps = {
  returning: boolean;
  daysSince: DaysSinceBucket;
};

/** Where the last visit's timestamp lives. Device-only; never sent. */
export const LAST_VISIT_KEY = "analytics:lastVisitAt";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Bucket the gap between two visits. Nothing finer than a bucket ever
 * leaves the device, and a clock that has moved backwards (a stored stamp in
 * the future) reads as same-day rather than as a negative gap.
 */
export function daysSinceBucket(lastVisitAt: number | null, now: number): DaysSinceBucket {
  if (lastVisitAt === null || !Number.isFinite(lastVisitAt)) return "none";
  const days = Math.floor(Math.max(0, now - lastVisitAt) / DAY_MS);
  if (days <= 1) return "1";
  if (days <= 7) return "2-7";
  if (days <= 30) return "8-30";
  return "30+";
}

/**
 * Read the stored last-visit stamp, derive this load's `visit` properties,
 * then write `now` back over it. Pure enough to test: both the clock and the
 * store are injectable, and the only thing it reports is a boolean and a
 * bucket name.
 */
export function readAndUpdateLastVisit(
  now: number = Date.now(),
  store: TypedStorage = appStorage,
): VisitProps {
  const last = store.get<number | null>(LAST_VISIT_KEY, null);
  const daysSince = daysSinceBucket(typeof last === "number" ? last : null, now);
  store.set(LAST_VISIT_KEY, now);
  return { returning: daysSince !== "none", daysSince };
}

/**
 * Record one page load (GRA book/measurement pass). Call once at startup:
 * main.tsx does it at module scope, which a React StrictMode double-mount
 * cannot repeat.
 */
export function trackVisit(now: number = Date.now(), store: TypedStorage = appStorage): void {
  send("visit", readAndUpdateLastVisit(now, store));
}

/**
 * A practice pack, by id (GRA pack work). The only property any pack event
 * carries besides its own shape: no email, no Checkout session id, no
 * customer id, no price, no amount. A purchase event answers "did one
 * happen", never "whose".
 */
export type PackProps = {
  pack: string;
};

export type PackExerciseCompletedProps = PackProps & {
  /** True when the exercise was one of the free samples. */
  sample: boolean;
  correct: boolean;
};

/** Record that a pack's page was opened. */
export function trackPackViewed(props: PackProps): void {
  send("pack_viewed", props);
}

/** Record one called pack exercise: free sample or paid, right or wrong. */
export function trackPackExerciseCompleted(props: PackExerciseCompletedProps): void {
  send("pack_exercise_completed", props);
}

/** Record that a Checkout session was requested for a pack. */
export function trackPackCheckoutStarted(props: PackProps): void {
  send("pack_checkout_started", props);
}

/** Record that a return from Checkout successfully claimed access. */
export function trackPackPurchaseCompleted(props: PackProps): void {
  send("pack_purchase_completed", props);
}

/** Record that the restore-access form recovered an existing purchase. */
export function trackPackAccessRestored(props: PackProps): void {
  send("pack_access_restored", props);
}

/** Test-only: the in-memory session counters do not otherwise reset. */
export function resetAnalyticsSessionForTests(): void {
  sessionRoundCount = 0;
  firedMilestones = new Set();
  shownPlacements = new Set();
}
